import { motion } from 'framer-motion';
import { CheckCircle2, Shield, Star, Award, Activity } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { PageHero } from '@/components/Cards';
import { useLanguage } from '@/hooks/useLanguage';

const products = [
  {
    id: 'shower-chair',
    icon: '🪑',
    name: 'Shower Chair / Bath Chair',
    nameTh: 'เก้าอี้อาบน้ำ / เก้าอี้ในห้องน้ำ',
nameZh: '',
    description: 'Ergonomically designed shower chairs with anti-slip feet, drainage holes, and adjustable height. Suitable for the elderly, post-surgery patients, and individuals with mobility limitations. Lightweight aluminum frame with durable plastic seat and back.',
    descriptionTh: 'เก้าอี้อาบน้ำออกแบบตามหลักการยศาสตร์พร้อมขาป้องกันการลื่น รูระบายน้ำ และความสูงปรับได้ เหมาะสำหรับผู้สูงอายุ ผู้ป่วยหลังผ่าตัด และผู้ที่มีข้อจำกัดด้านการเคลื่อนไหว โครงอะลูมิเนียมน้ำหนักเบาพร้อมที่นั่งและพนักพิงพลาสติกทนทาน',
    images: ['/images/shower_chair_1.jpg', '/images/shower_chair_2.jpg', '/images/shower_chair_3.jpg', '/images/shower_chair_4.jpg'],
    mainImage: '/images/shower_chair_1.jpg',
    features: [
      { en: 'Adjustable height legs', th: 'ขาปรับความสูงได้' },
      { en: 'Anti-slip rubber feet', th: 'ขาพลาสติกกันลื่น' },
      { en: 'Drainage holes for water flow', th: 'รูระบายน้ำ' },
      { en: 'Lightweight aluminum frame', th: 'โครงอะลูมิเนียมน้ำหนักเบา' },
      { en: 'Armrests for support', th: 'ที่วางแขนเพื่อรองรับ' },
    ],
  },
  {
    id: 'crutches',
    icon: '🩼',
    name: 'Crutches (Axillary & Forearm)',
    nameTh: 'ไม้เท้า (แบบรักแร้และแบบปลายแขน)',
nameZh: '',
    description: 'Medical-grade aluminum crutches available in axillary (underarm) and forearm (elbow) styles. Adjustable height with secure locking mechanism, ergonomic hand grips, and durable non-slip rubber tips. Designed for post-surgery recovery, fractures, and mobility assistance.',
    descriptionTh: 'ไม้เท้าอะลูมิเนียมระดับการแพทย์มีทั้งแบบรักแร้และแบบปลายแขน ปรับความสูงได้ด้วยกลไกล็อคที่ปลอดภัย ที่จับตามหลักการยศาสตร์ และปลายยางกันลื่นทนทาน ออกแบบมาสำหรับการฟื้นตัวหลังผ่าตัด กระดูกหัก และการช่วยเดิน',
    images: ['/images/crutches_1.jpg'],
    mainImage: '/images/crutches_1.jpg',
    features: [
      { en: 'Height-adjustable aluminum frame', th: 'โครงอะลูมิเนียมปรับความสูงได้' },
      { en: 'Ergonomic hand grip', th: 'ที่จับตามหลักการยศาสตร์' },
      { en: 'Non-slip rubber tip', th: 'ปลายยางกันลื่น' },
      { en: 'Underarm pad (axillary type)', th: 'แผ่นรองรักแร้ (แบบรักแร้)' },
      { en: 'Forearm cuff (elbow type)', th: 'ที่รัดปลายแขน (แบบปลายแขน)' },
    ],
  },
  {
    id: 'rollator',
    icon: '🦽',
    name: 'Rollator Walker (Wheeled Walking Frame)',
    nameTh: 'วอล์กเกอร์มีล้อ / ไม้เท้ามีล้อช่วยเดิน',
nameZh: '',
    description: 'Premium 4-wheeled rollator walkers with forearm supports, hand brakes, and storage basket. Provides superior stability and independence for elderly users and those with balance or mobility challenges. Lightweight but sturdy aluminum construction with adjustable handle height.',
    descriptionTh: 'วอล์กเกอร์มีล้อ 4 ล้อพรีเมียมพร้อมที่รองรับปลายแขน เบรคมือ และตะกร้าเก็บของ ให้ความมั่นคงและความเป็นอิสระที่เหนือกว่าสำหรับผู้สูงอายุและผู้ที่มีปัญหาด้านการทรงตัวหรือการเคลื่อนไหว โครงอะลูมิเนียมน้ำหนักเบาแต่แข็งแรงพร้อมความสูงมือจับปรับได้',
    images: ['/images/rollator_1.jpg'],
    mainImage: '/images/rollator_1.jpg',
    features: [
      { en: '4 swivel rubber wheels', th: 'ล้อยางหมุน 4 ล้อ' },
      { en: 'Forearm support platforms', th: 'แท่นรองรับปลายแขน' },
      { en: 'Hand-operated locking brakes', th: 'เบรคล็อคด้วยมือ' },
      { en: 'Underseat storage basket', th: 'ตะกร้าเก็บของใต้ที่นั่ง' },
      { en: 'Adjustable handle height', th: 'ความสูงมือจับปรับได้' },
    ],
  },
  {
    id: 'toilet-aid',
    icon: '🚽',
    name: 'Toilet Aid Equipment (Raised Toilet Seat)',
    nameTh: 'อุปกรณ์ช่วยชำระร่างกาย (ที่นั่งชักโครกยกสูง)',
nameZh: '',
    description: 'Raised toilet seats and commode chairs providing elevated seating height to assist individuals with limited mobility, joint pain, or post-surgical needs. Features padded armrests, stable frame construction, and anti-slip base. Reduces strain on hips and knees during seated transfers.',
    descriptionTh: 'ที่นั่งชักโครกยกสูงและเก้าอี้โถส้วมที่ให้ความสูงในการนั่งเพิ่มขึ้นเพื่อช่วยเหลือผู้ที่มีการเคลื่อนไหวจำกัด ปวดข้อ หรือมีความต้องการหลังผ่าตัด มีที่วางแขนนุ่ม โครงสร้างที่มั่นคง และฐานกันลื่น ลดแรงกดบริเวณสะโพกและเข่าระหว่างการเปลี่ยนท่า',
    images: ['/images/toilet_aid_1.jpg'],
    mainImage: '/images/toilet_aid_1.jpg',
    features: [
      { en: 'Adjustable height frame', th: 'โครงปรับความสูงได้' },
      { en: 'Padded armrests for support', th: 'ที่วางแขนนุ่มรองรับ' },
      { en: 'Non-slip rubber base', th: 'ฐานยางกันลื่น' },
      { en: 'Easy-clean surface', th: 'พื้นผิวทำความสะอาดง่าย' },
      { en: 'Universal fit design', th: 'ออกแบบให้พอดีกับทุกขนาด' },
    ],
  },
];

const compliance = [
  { icon: '🏥', label: 'Class I Medical Device', labelTh: 'อุปกรณ์การแพทย์ Class I' },
  { icon: '✅', label: 'Thai FDA Registered', labelTh: 'ขึ้นทะเบียน อย. ไทย' },
  { icon: '🇪🇺', label: 'CE Marked (EU Standard)', labelTh: 'เครื่องหมาย CE (มาตรฐานยุโรป)' },
  { icon: '📋', label: 'ISO 13485 Aligned', labelTh: 'สอดคล้องกับ ISO 13485' },
];

export default function MedicalDevices() {
  const { t } = useLanguage();

  return (
    <Layout>
      <PageHero
        title="Medical Devices"
        titleTh="อุปกรณ์การแพทย์"
        subtitle="Class I Medical Devices for Mobility & Daily Living Support"
        subtitleTh="อุปกรณ์การแพทย์ Class I สำหรับการเคลื่อนไหวและการดำรงชีวิตประจำวัน"
        image="/images/shower_chair_1.jpg"
      />

      {/* Overview */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <Shield className="w-4 h-4" />
                {t('Class I Medical Devices', 'อุปกรณ์การแพทย์ Class I')}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-5">
                {t('Mobility Assistance & Daily Living Support', 'อุปกรณ์ช่วยเคลื่อนไหวและการดำรงชีวิตประจำวัน')}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-5">
                {t(
'Yaruwat distributes high-quality Class I medical devices () focused on mobility assistance and activities of daily living. Our product range includes shower chairs, crutches, rollator walkers, and toilet aid equipment  all certified to international standards and registered with Thai FDA.',
'ยารุวาทจัดจำหน่ายอุปกรณ์การแพทย์ Class I () คุณภาพสูง ที่มุ่งเน้นการช่วยเดินและกิจกรรมในชีวิตประจำวัน ผลิตภัณฑ์ของเราได้แก่ เก้าอี้อาบน้ำ ไม้เท้า วอล์กเกอร์มีล้อ และอุปกรณ์ช่วยชำระร่างกาย ทั้งหมดได้รับการรับรองตามมาตรฐานสากลและขึ้นทะเบียนกับ อย. ไทย'
                )}
              </p>
              <p className="text-muted-foreground leading-relaxed">
                {t(
                  'These devices are designed to enhance independence, safety, and comfort for the elderly, post-surgery patients, and individuals with reduced mobility — empowering them to live with dignity and confidence.',
                  'อุปกรณ์เหล่านี้ออกแบบมาเพื่อเพิ่มความเป็นอิสระ ความปลอดภัย และความสะดวกสบายสำหรับผู้สูงอายุ ผู้ป่วยหลังผ่าตัด และผู้ที่มีข้อจำกัดด้านการเคลื่อนไหว ช่วยให้พวกเขาใช้ชีวิตด้วยศักดิ์ศรีและความมั่นใจ'
                )}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              {/* Compliance badges */}
              <div className="grid grid-cols-2 gap-3">
                {compliance.map((item) => (
                  <div
                    key={item.label}
                    className="bg-white rounded-xl p-4 shadow-sm border border-border/50 flex items-center gap-3"
                  >
                    <span className="text-2xl">{item.icon}</span>
                    <span className="text-sm font-medium text-foreground">{t(item.label, item.labelTh)}</span>
                  </div>
                ))}
              </div>
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Activity className="w-5 h-5 text-primary" />
                  <span className="font-semibold text-foreground">
                    {t('Product Classification', 'การจำแนกประเภทผลิตภัณฑ์')}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {t(
                    'All products are classified as Class I (low-risk) medical devices under Thai FDA regulations. They are intended for home use, hospitals, and rehabilitation centers.',
                    'ผลิตภัณฑ์ทั้งหมดจัดอยู่ในอุปกรณ์การแพทย์ Class I (ความเสี่ยงต่ำ) ตามข้อบังคับ อย. ไทย ออกแบบมาสำหรับการใช้งานในบ้าน โรงพยาบาล และศูนย์ฟื้นฟูสมรรถภาพ'
                  )}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-primary font-semibold text-sm uppercase tracking-widest">
              {t('Product Range', 'ช่วงผลิตภัณฑ์')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2 mb-3">
              {t('Our Class I Medical Devices', 'อุปกรณ์การแพทย์ Class I ของเรา')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t(
                'Four product categories designed to support mobility, hygiene, and daily independence for patients and elderly individuals.',
                'สี่หมวดผลิตภัณฑ์ที่ออกแบบมาเพื่อรองรับการเคลื่อนไหว สุขอนามัย และความเป็นอิสระในชีวิตประจำวันสำหรับผู้ป่วยและผู้สูงอายุ'
              )}
            </p>
          </motion.div>

          <div className="space-y-12">
            {products.map((product, i) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-white rounded-2xl overflow-hidden shadow-md border border-border/50"
              >
                <div className={`flex flex-col ${i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                  {/* Image */}
                  <div className="lg:w-2/5 relative overflow-hidden">
                    <img
                      src={product.mainImage}
                      alt={product.name}
                      className="w-full h-64 lg:h-full object-cover"
                    />
                    {/* Show thumbnail row for shower chairs */}
                    {product.images.length > 1 && (
                      <div className="absolute bottom-3 left-3 right-3 flex gap-2">
                        {product.images.slice(1, 4).map((img, j) => (
                          <div key={j} className="w-16 h-12 rounded-lg overflow-hidden border-2 border-white shadow-sm flex-shrink-0">
                            <img src={img} alt="" className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="absolute top-4 left-4 bg-primary text-primary-foreground text-xs font-bold px-3 py-1.5 rounded-full">
                      {t('Class I', 'Class I')}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="lg:w-3/5 p-6 lg:p-8 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-4xl">{product.icon}</span>
                      <div>
                        <h3 className="text-xl font-bold text-foreground">{t(product.name, product.nameTh)}</h3>
                        <span className="text-muted-foreground text-sm">{product.nameZh}</span>
                      </div>
                    </div>

                    <p className="text-muted-foreground mb-5 leading-relaxed">
                      {t(product.description, product.descriptionTh)}
                    </p>

                    {/* Features */}
                    <div>
                      <h4 className="font-semibold text-foreground text-sm mb-2 flex items-center gap-1">
                        <Award className="w-4 h-4 text-primary" />
                        {t('Key Features', 'คุณสมบัติหลัก')}
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {product.features.map((f) => (
                          <div key={f.en} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                            {t(f.en, f.th)}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Rating */}
                    <div className="flex items-center gap-1 mt-4">
                      {[1,2,3,4,5].map((s) => (
                        <Star key={s} className="w-4 h-4 fill-accent text-accent" />
                      ))}
                      <span className="text-sm text-muted-foreground ml-2">
                        {t('Medical Grade Quality', 'คุณภาพระดับการแพทย์')}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Users */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-foreground mb-3">
              {t('Who These Devices Are For', 'อุปกรณ์เหล่านี้เหมาะสำหรับใคร')}
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: '👴', title: 'Elderly Individuals', titleTh: 'ผู้สูงอายุ', desc: 'Support aging in place and daily independence', descTh: 'สนับสนุนการดูแลผู้สูงอายุในบ้านและความเป็นอิสระในชีวิตประจำวัน' },
              { icon: '🏥', title: 'Post-Surgery Recovery', titleTh: 'ฟื้นตัวหลังผ่าตัด', desc: 'Rehabilitation after orthopedic and joint surgeries', descTh: 'ฟื้นฟูหลังการผ่าตัดกระดูกและข้อต่อ' },
              { icon: '♿', title: 'Mobility-Impaired Users', titleTh: 'ผู้มีข้อจำกัดด้านการเคลื่อนไหว', desc: 'Assistive devices for limited mobility conditions', descTh: 'อุปกรณ์ช่วยเหลือสำหรับภาวะการเคลื่อนไหวจำกัด' },
              { icon: '🏨', title: 'Care Facilities', titleTh: 'สถานดูแลผู้ป่วย', desc: 'Hospitals, nursing homes, and rehabilitation centers', descTh: 'โรงพยาบาล บ้านพักคนชรา และศูนย์ฟื้นฟูสมรรถภาพ' },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-sm border border-border/50 text-center"
              >
                <div className="text-5xl mb-3">{item.icon}</div>
                <h3 className="font-bold text-foreground mb-2">{t(item.title, item.titleTh)}</h3>
                <p className="text-sm text-muted-foreground">{t(item.desc, item.descTh)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            {t('Interested in Our Medical Device Range?', 'สนใจผลิตภัณฑ์อุปกรณ์การแพทย์ของเราหรือไม่?')}
          </h2>
          <p className="text-primary-foreground/80 mb-8">
            {t(
              'Contact us for product catalogs, pricing, and distribution partnership opportunities.',
              'ติดต่อเราเพื่อขอแคตตาล็อกสินค้า ราคา และโอกาสความร่วมมือด้านการจัดจำหน่าย'
            )}
          </p>
          <a
            href="/#/contact"
            className="inline-flex items-center gap-2 bg-white text-primary font-semibold px-8 py-4 rounded-full hover:bg-white/90 transition-all shadow-lg"
          >
            {t('Get in Touch', 'ติดต่อเรา')}
          </a>
        </div>
      </section>
    </Layout>
  );
}
